<template>
  <div id="associated-leads" class="associated-leads p-4">
    <div class="row align-items-center align-item-middle pb-4">
      <div class="col-6">
        <h3 class="mb-0">Associated Leads</h3>
      </div>
      <div class="col-6 text-right">
        <button class="btn btn-sm btn-primary" @click="showAddModal = true">
          + Add
        </button>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-12">
        <div v-loading="loading">
            <ul class="list-group list-group-flush list my--3" v-if="delegates.length">
            <li
                v-for="delegate in delegates"
                :key="delegate.id"
                class="list-group-item px-0 delegate-row"
            >
                <div class="row align-items-center">
                <div class="col-auto">
                    <span class="avatar rounded-circle">
                        <img src="/img/custom/user.png" />
                    </span>
                </div>
                <div class="col ml--2">
                    <h5 class="mb-0">
                    <a :href="`/leads/${delegate.id}`" target="_blank">{{ delegate.first_name }} {{ delegate.last_name }}</a>
                    <span v-if="delegate.is_owner" class="badge badge-primary ml-2">Owner</span>
                    </h5>
                    <small>{{ delegate.email }}</small>
                </div>
                <div class="col-auto">
                    <button
                        v-if="! delegate.is_owner"
                        class="btn btn-sm btn-outline-primary mr-2 make-owner-btn"
                        @click="makeOwner(delegate)"
                        title="Make Owner"
                    >
                        <i class="fas fa-user-check"></i> Make Owner
                    </button>
                    <button
                        v-if="!delegate.is_owner"
                        class="btn btn-sm btn-outline-danger"
                        @click="removeDelegate(delegate)"
                        title="Remove Delegate"
                    >
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                </div>
            </li>
            </ul>
            <div v-else class="text-center py-4 text-muted">
                No Associated Leads
            </div>
        </div>
      </div>
    </div>

    <el-dialog
      title="Add lead to event"
      :visible.sync="showAddModal"
      width="500px"
      append-to-body
      :close-on-click-modal="false"
    >
      <add-delegate-modal
        v-if="showAddModal"
        :lead-event="leadEvent"
        @close="showAddModal = false"
        @added="onDelegateAdded"
      />
    </el-dialog>
  </div>
</template>

<script>
import LeadEventService from "@/services/lead-event.service";
import AddDelegateModal from "./AddDelegateModal.vue";
import { MessageBox } from "element-ui";

export default {
  name: "AssociatedLeads",
  components: {
    AddDelegateModal,
  },
  props: {
    leadEvent: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      delegates: [],
      showAddModal: false,
    };
  },
  created() {
    this.fetchDelegates();
  },
  methods: {
    async fetchDelegates() {
      this.loading = true;
      try {
        const response = await LeadEventService.getDelegates(this.leadEvent.id);
        this.delegates = response.data?.delegates || [];
      } catch (error) {
        console.error("Failed to fetch delegates:", error);
        this.$store.dispatch("alert/error", "Failed to load associated leads.", { root: true });
      } finally {
        this.loading = false;
      }
    },
    async removeDelegate(delegate) {
      MessageBox.confirm(
        `Are you sure you want to remove ${delegate.first_name} ${delegate.last_name} from this event?`,
        "Confirm Removal",
        {
          confirmButtonText: "Remove",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      )
        .then(async () => {
          this.loading = true;
          try {
            await LeadEventService.removeDelegate(
              this.leadEvent.id,
              this.leadEvent.lead_id,
              delegate.id
            );
            this.$store.dispatch("alert/success", "Delegate removed successfully.", { root: true });
            await this.fetchDelegates();
          } catch (error) {
            console.error("Failed to remove delegate:", error);
            this.$store.dispatch("alert/error", "Failed to remove delegate.", { root: true });
          } finally {
            this.loading = false;
          }
        })
        .catch(() => {});
    },
    async makeOwner(delegate) {
      MessageBox.confirm(
        `Are you sure you want to make ${delegate.first_name} ${delegate.last_name} (${delegate.email}) the owner of this event?`,
        "Confirm Owner Change",
        {
          confirmButtonText: "Make Owner",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      )
        .then(async () => {
          this.loading = true;
          try {
            await LeadEventService.makeDelegateOwner(
              this.leadEvent.id,
              this.leadEvent.lead_id,
              delegate.id
            );
            // Keep local leadEvent.lead_id in sync with the new owner.
            this.leadEvent.lead_id = delegate.id;
            this.$store.dispatch("alert/success", "Owner updated successfully.", { root: true });
            this.$emit("owner-changed", delegate);
            await this.fetchDelegates();
          } catch (error) {
            console.error("Failed to update owner:", error);
            this.$store.dispatch("alert/error", "Failed to update owner.", { root: true });
          } finally {
            this.loading = false;
          }
        })
        .catch(() => {});
    },
    onDelegateAdded() {
      this.showAddModal = false;
      this.fetchDelegates();
    },
  },
};
</script>

<style scoped>
.associated-leads {
    background: #fff; /* Match other widgets */
}

.delegate-row .make-owner-btn {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
}

.delegate-row:hover .make-owner-btn,
.delegate-row .make-owner-btn:focus {
    visibility: visible;
    opacity: 1;
}
</style>
